import orderStyles from "@crm/styles/order.module.css";
import {windowCleaningActions, WindowCleaningPage} from "@crm/services/window-cleaning-slice";
import {AvailabilityOption} from "@crm/components/calendar/calendar-components/date-options/date-options";
import React, {useEffect} from "react";
import {housekeeprApi} from "@crm/api/housekeepr-api";
import {skipToken} from "@reduxjs/toolkit/query";
import {useAppDispatch, useAppSelector} from "@crm/services/store";
import {
    selectWindowCleaningAddress,
    selectWindowCleaningAvailability,
    selectWindowCleaningFrequencyId,
    selectWindowCleaningMaxPage,
    selectWindowCleaningResidenceType,
    selectWindowCleaningSecondaryGlazing,
    selectWindowCleaningServiceId,
    selectWindowCleaningServiceTypeId,
    selectWindowCleaningWindows
} from "@crm/services/window-cleaning-selectors";
import {LoadingDots} from "@crm/components/icon/loading-dots";
import Typography from "@crm/components/typography/typography";
import Calendar from "@crm/components/calendar/calendar";
import {useDebounce} from "@crm/hooks/use-debounce";
import {frequencyDisplayString} from "@crm/api/models/common/housekeepr-api";
import {smoothScrollToTop} from "@crm/utils/helpers";
import {AvailabilityRequestForChunking} from "@crm/api/models/requests/housekeepr-api";

type WindowCleaningTimeCardProps = { hidden?: boolean };

export const WindowCleaningTimeCard = (props: WindowCleaningTimeCardProps) => {
    const dispatch = useAppDispatch();

    const maxPage = useAppSelector(selectWindowCleaningMaxPage);

    const selectedServiceTypeId = useAppSelector(selectWindowCleaningServiceTypeId);
    const selectedAddress = useAppSelector(selectWindowCleaningAddress);
    const selectedServiceId = useAppSelector(selectWindowCleaningServiceId);
    const selectedFrequencyId = useAppSelector(selectWindowCleaningFrequencyId);
    const selectedResidenceType = useAppSelector(selectWindowCleaningResidenceType);
    const selectedWindowTypes = useAppSelector(selectWindowCleaningWindows);
    const hasSecondaryGlazing = useAppSelector(selectWindowCleaningSecondaryGlazing);
    const selectedAvailability = useAppSelector(selectWindowCleaningAvailability);

    const {
        data: frequenciesResult,
    } = housekeeprApi.useGetFrequenciesQuery(selectedServiceId ?? skipToken);

    const frequency = frequenciesResult?.find(frequency => frequency.id == selectedFrequencyId);

    const {
        data: serviceResult
    } = housekeeprApi.useGetServiceQuery(selectedServiceId ?? skipToken);

    const {
        data: addressResult,
    } = housekeeprApi.useRegisterAddressQuery(selectedAddress
        ? {
            darId: selectedAddress.adresse.id,
        }
        : skipToken
    );

    const debouncedWindows = useDebounce(selectedWindowTypes, 3000);

    const canSearchAvailability = !!selectedServiceId && !!selectedFrequencyId && !!selectedResidenceType && debouncedWindows.length > 0 && !!addressResult && !!serviceResult && !!frequency;

    const availabilityRequest = canSearchAvailability ? {
        service_id: selectedServiceId,
        address_id: addressResult.id,
        frequency_id: selectedFrequencyId,
        has_secondary_glazing: hasSecondaryGlazing,
        residence_type: selectedResidenceType,
        windows: debouncedWindows,
        search_period: serviceResult.search_period,
        search_chunks: frequency.search_chunks
    } as AvailabilityRequestForChunking : undefined;

    const {
        data: availabilityResult,
        isFetching: isFetchingAvailability,
        isUninitialized: isUninitializedAvailability,
        error: availabilityError,
    } = housekeeprApi.useGetServiceAvailabilitySplitQuery(availabilityRequest ?? skipToken);

    useEffect(() => {
        if (selectedAvailability && availabilityResult && !availabilityResult.some(availability => availability.time_slot_id == selectedAvailability.time_slot_id && availability.schedule_id == selectedAvailability.schedule_id)) {
            const matchingAvailability = availabilityResult.find(availability => availability.time_slot_id == selectedAvailability.time_slot_id && availability.schedule_id == selectedAvailability.schedule_id);
            dispatch(matchingAvailability ? windowCleaningActions.setAvailability(matchingAvailability) : windowCleaningActions.clearAvailability());
        } else if (selectedAvailability && availabilityError) {
            dispatch(windowCleaningActions.clearAvailability());
        }
    });

    useEffect(() => {
        if (selectedFrequencyId && frequenciesResult && !frequenciesResult.some(frequency => frequency.id == selectedFrequencyId)) {
            dispatch(frequenciesResult[0] ? windowCleaningActions.setFrequency(frequenciesResult[0]) : windowCleaningActions.clearFrequency());
        }
    }, [dispatch, frequenciesResult, selectedFrequencyId, selectedServiceId]);

    return <div className={props.hidden ? orderStyles.hidden : orderStyles.cardWrapper}>
        <div className={orderStyles.card}>
            <div className={orderStyles.orderSection}>
                <h1 className={`${orderStyles.cardH1}`}>
                    Hvor ofte skal vi komme?
                </h1>
                <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                    Med en fast aftale sikrer vi, at dine vinduer altid er rene. Vi informerer dig, når det er tid til
                    den næste vinduespudsning.
                    Dine fremtidige besøg bliver automatisk booket 3 uger før den planlagte dato.
                    Du vil modtage en notifikation i Housekeepr app&apos;en, når dit næste besøg er planlagt.
                    I app&apos;en har du også mulighed for gratis at flytte dine besøg, hvis det planlagte
                    tidspunkt ikke passer ind i din kalender.
                </p>
                <div
                    id={'frequency-content'}
                    className={`${orderStyles.orderSectionContent} ${orderStyles.flexRow} ${orderStyles.flexWrap}`}>
                    {frequenciesResult?.map(frequency => {
                        return (
                            <div
                                className={`${orderStyles.cardOption} ${selectedFrequencyId == frequency.id ? orderStyles.selected : ''}`}
                                onClick={() => dispatch(windowCleaningActions.setFrequency(frequency))}
                                key={frequency.id}
                                id={`frequency-${frequency.id}`}
                            >
                                {frequencyDisplayString(frequency)}
                            </div>
                        );
                    })}
                </div>
            </div>
            <div className={orderStyles.orderSection} id={'address-content'}>
                <h1 className={`${orderStyles.cardH1}`}>
                    {frequency?.every == 0 ? 'Hvornår skal vi komme?' : 'Hvornår skal vi komme første gang?'}
                </h1>
                <p className={`${orderStyles.orderSectionContent} ${orderStyles.removeTopMargin}`}>
                    Vi gør alt hvad vi kan for at ankomme i det valgte tidsrum, men der kan opstå afvigelser på op til
                    30 minutter. I Housekeepr app&apos;en kan du altid se, hvornår vi er på vej.
                </p>
                {selectedFrequencyId && ((isUninitializedAvailability || isFetchingAvailability || !selectedServiceTypeId)
                        ? <div
                            className={`${orderStyles.calendarWrapper} ${orderStyles.flexColumn} ${orderStyles.flexMainCenter} ${orderStyles.flexCrossCenter} h-[300px]`}>
                            <LoadingDots/>
                            <Typography variant={'body'} className={'mt-4'}>Vent venligst mens vi indlæser ledige
                                tider...</Typography>
                        </div>
                        : <div className={orderStyles.calendarWrapper}>
                            {availabilityError
                                ?
                                <div className={orderStyles.orderHandler}>
                                    <Typography
                                        variant={'body'}
                                        textAlign={'center'}
                                    >
                                        <p style={{whiteSpace: "pre-wrap"}}>
                                            {(availabilityError as any)?.data?.error?.toString()?.replace('. ', '.\n') ?? ' Der opstod desværre en fejl i søgning efter tid. Prøv igen senere.'}
                                        </p>
                                    </Typography>
                                </div>
                                : <Calendar
                                    availabilitySelector={selectWindowCleaningAvailability}
                                    dateOptions={availabilityResult?.map(availability => ({
                                        text: availability.date,
                                        availableDate: new Date(Date.parse(availability.date)),
                                        availability: availability,
                                    } as AvailabilityOption)) ?? []}
                                    dateOptionRows={3}
                                    multipleDateOptions={false}
                                    onSelectOption={(availabilityOption) => {
                                        dispatch(availabilityOption
                                            ? windowCleaningActions.setAvailability(availabilityOption.availability)
                                            : windowCleaningActions.clearAvailability()
                                        );
                                    }}
                                    serviceTypeId={selectedServiceTypeId}
                                />
                            }
                        </div>
                )}
            </div>
        </div>
        <div className={`${orderStyles.flexRow} ${orderStyles.flexMainSpaceBetween} self-stretch`}>
            <div
                className={`${orderStyles.buttonWrapper} ${orderStyles.outline} w-[200px]`}>
                <button
                    onClick={() => {
                        dispatch(windowCleaningActions.setPage(WindowCleaningPage.WINDOWS));
                        smoothScrollToTop();
                    }}
                >
                    Tilbage
                </button>
            </div>
            <div
                className={`${orderStyles.buttonWrapper} ${orderStyles.outline} ${maxPage < WindowCleaningPage.USER ? orderStyles.disabled : ''} w-[200px]`}>
                <button
                    onClick={() => {
                        dispatch(windowCleaningActions.setPage(WindowCleaningPage.USER));
                        smoothScrollToTop();
                    }}
                    disabled={maxPage < WindowCleaningPage.USER}
                >
                    Videre
                </button>
            </div>
        </div>
    </div>;
}

export default WindowCleaningTimeCard;